import * as vp from 'vopay';

import { createContext, ReactNode, useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import { useFetchBillingPackagesDetails, useFetchBillingSettingsDetails } from '../hooks';
import usePartnerBearer from '../hooks/useApi/usePartnerBearer';

export const BillingDetailsContext = createContext<vp.SettingsGroups>({});

export const BillingDetailsProvider = ({ children }: { children: ReactNode }): React.JSX.Element => {
  const { resource, id } = useParams<{ resource: string; id: string }>();
  const { search } = useLocation();
  const partnerAccountId = new URLSearchParams(search).get('partnerAccountId') ?? '';
  const partnerBearer = usePartnerBearer(partnerAccountId);

  const [{ packageDetails }, getPackageDetails] = useFetchBillingPackagesDetails({ bearer: partnerBearer });
  const [{ settingsDetails }, getSettingsDetails] = useFetchBillingSettingsDetails({ bearer: partnerBearer });

  useEffect(() => {
    if (!partnerBearer) return;

    const handlers: { [resource: string]: () => void } = {
      package: () => getPackageDetails(id),
      settings: () => getSettingsDetails(id),
      default: () => {},
    };

    const handler = handlers[resource] || handlers.default;

    handler();
  }, [partnerBearer]);

  function getBillingDetails(): vp.SettingsGroups {
    const handlers: { [resource: string]: vp.SettingsGroups } = {
      package: packageDetails,
      settings: settingsDetails,
      default: {},
    };

    return handlers[resource] || handlers.default;
  }

  return <BillingDetailsContext.Provider value={getBillingDetails()}>{children}</BillingDetailsContext.Provider>;
};
